import { makeAutoObservable, observable, runInAction } from "mobx";
import {
  getUserUrl,
  logoutUrl,
  updateUserProfileUrl,
} from "../../components/constants";
import { getLocalStorage } from "../../utils";
import { removeCookie } from "../../utils/cookie";
import apiRequest from "../../utils/apiClient";
import { isNativeApp } from "../../components/mobile/native";

class UserStore {
  user = {};
  phase = "get_email"; // get_email, get_code, login
  token = null;
  transcript = { downloadId: null, title: "", transcriptId: null };

  // possible move this to a store for the subtitles
  previousWord = -1;
  sentenceStart = -1;
  intervalId = null;
  timeOutId = null;
  timeUpdateHandler = null;
  video = null;
  item = null;
  data = null;
  durationInMs = 0;
  startTime = 0;
  //
  duration = 0;
  totalDuration = 0;
  constructor() {
    makeAutoObservable(this, {
      user: observable,
      phase: observable,
      token: observable,
      transcript: observable,
      previousWord: observable,
      sentenceStart: observable,
      intervalId: observable,
      timeOutId: observable,
      video: observable,
      startTime: observable,
      duration: observable,
      totalDuration: observable,
    });
    this.token = getLocalStorage("token");
    // Never run the auth bootstrap when this app instance is inside an iframe (the embedded
    // live editor on the home page). localStorage is shared same-origin, so a failed call here
    // would clear the REAL tab's token and log the user out. See iframe-embed-logout-gotcha.
    const inIframe = typeof window !== "undefined" && window.self !== window.top;
    // Same reasoning for the phone app: it signs in through its own screens and keeps its own
    // token. This bootstrap firing there meant one 401 on launch silently deleted a perfectly
    // good session, and the app then looked signed in while holding nothing.
    if (this.token !== null && !inIframe && !isNativeApp()) this.getUserInfo();
  }

  getUserInfo = async () => {
    const result = await apiRequest(getUserUrl);
    const data = await result.json();

    if (result.status === 200) {
      this.setUser(data.user);
    } else if (result.status === 401 || result.status === 403) {
      // ONLY a real auth failure logs the user out. A transient 5xx (server error)
      // must NOT nuke a valid session — doing so made logged-in users look logged
      // out (e.g. the subscribe guard blocked them with "please login").
      // Guard: inside an iframe, do NOT clear the token — it's shared with the parent tab.
      const inIframe = typeof window !== "undefined" && window.self !== window.top;
      if (!inIframe) {
        this.setToken(null);
        localStorage.removeItem("token");
        removeCookie("token");
      }
    }
  };

  updateUserProfile = async (formData) => {
    const response = await apiRequest(updateUserProfileUrl, {
      method: "POST",
      body: formData,
    });
    const result = await response.json();
    if (response.status === 200) {
      this.setUser(result.user);
    }
  };

  logoutBackEnd = async () => {
    const response = await apiRequest(logoutUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });
    const result = await response.json();
    if (result.message === "ok") {
      this.setUser({});
      this.setPhase("get_email");
      this.setToken(null);
      localStorage.removeItem("token");
      removeCookie("token");
    }
  };

  setUser = (user) => (this.user = user);

  getUser = () => this.user;

  getUserId = () => this.user.uuid;

  getUserEmail = () => this.user.email;

  getUserNickname = () => this.user.nickname;

  getUserFirstName = () => this.user.first_name;

  getUserLastName = () => this.user.last_name;

  getUserFullName = () => this.user.full_name;

  // CAREFUL: this is NOT "is a paying customer". Every free user gets a StripeCustomer row
  // with is_active=True the moment they log in (ensure_free_plan in emo_users/utils/
  // auth_helpers.py), so this is true for free accounts too. It effectively means "has a
  // usable billing record", and library/app access keys off it — do not change that meaning.
  // To ask "are they PAYING?", use isUserOnPaidPlan below.
  isUserStripeActive = () => {
    if (this.user === null || this.user === undefined) return false;
    return this.user.is_stripe_active === true;
  };

  // True only on a real paid plan. The subscribe button needs this: it used to check
  // isUserStripeActive and therefore told EVERY free user "You are already subscribed",
  // which made it impossible for anyone to buy.
  isUserOnPaidPlan = () => {
    if (this.user === null || this.user === undefined) return false;
    const plan = (this.user.subscription_plan || "").trim().toLowerCase();
    return this.user.is_stripe_active === true && plan !== "" && plan !== "free";
  };

  planName = () => {
    if (this.user === null || this.user === undefined) return "";
    return (this.user.subscription_plan || "").trim().toLowerCase();
  };

  getUserPlan = () => this.user.subscription_plan;

  getUserStripeCustomerId = () => this.user.stripe_customer_id;

  getUserStripeSubscriptionId = () => this.user.stripe_subscription_id;

  setUserTranscript = (downloadId, title, transcriptId, size) =>
    (this.transcript = {
      downloadId: downloadId,
      title: title,
      transcriptId: transcriptId,
      size: size,
    });

  getUserDownloadId = () => this.transcript["downloadId"];

  getUserTranscriptId = () => {
    return this.transcript["transcriptId"];
  };

  getUserTranscriptTitle = () => this.transcript["title"];

  getUserTranscriptSize = () => this.transcript["size"];

  // Trust localStorage too (the same token the API client authenticates with),
  // not just the in-memory copy — otherwise a transient state where this.token
  // got nulled makes a genuinely-logged-in user look logged out (which blocked
  // the subscribe button with "please login").
  isUserAuth = () => this.token !== null || getLocalStorage("token") != null;

  isUserSubscribed = () => {
    if (this.user === null || this.user === undefined) return false;
    // User is subscribed if they have a subscription plan and are stripe active
    return this.user.subscription_plan && this.user.is_stripe_active === true;
  };

  setToken = (token) => (this.token = token);

  getToken = () => this.token;

  setPhase = (phase) => (this.phase = phase);

  getPhase = () => this.phase;

  // refactor this to a store for the subtitles
  setPreviousWord = (previousWord) => (this.previousWord = previousWord);

  getPreviousWord = () => this.previousWord;

  setSentenceStart = (sentenceStart) => (this.sentenceStart = sentenceStart);

  getSentenceStart = () => this.sentenceStart;

  getSentenceEnd = () => this.data.end_in;

  setIntervalId = (intervalId) => (this.intervalId = intervalId);

  getIntervalId = () => this.intervalId;

  setTimeOutId = (timeOutId) => (this.timeOutId = timeOutId);

  getTimeOutId = () => this.timeOutId;

  setVideo = (data, video, item) => {
    this.data = data;
    this.video = video;
    this.item = item;
    this.sentenceStart = data.start_in;
  };
  getVideo = () => this.video;

  getVideoDurationMs = () =>
    this.data === null ? null : this.data.duration_ms;

  setTotalDuration = (duration) => {
    runInAction(() => {
      this.totalDuration = duration;
    });
  };

  getTotalDuration = () => this.totalDuration;

  setDurationInMs = (duration) => {
    runInAction(() => {
      this.durationInMs = duration;
    });
  };

  getDurationInMs = () => this.durationInMs;

  getItem = () => this.item;

  getVideoData = () => this.data;

  getStartIn = () => this.data.start_in;

  getEndIn = () => this.data.end_in;

  setTimeUpdateHandler = (handler) => (this.timeUpdateHandler = handler);

  getTimeUpdateHandler = () => this.timeUpdateHandler;

  resetVideo = () => {
    this.data = null;
    this.video = null;
    this.item = null;
    this.previousWord = -1;
    this.sentenceStart = -1;
    this.intervalId = null;
    this.timeUpdateHandler = null;
  };

  setStartTime = (time) => (this.startTime = time);

  getStartTime = () => this.startTime;

  setDuration = (duration) => {
    runInAction(() => {
      this.duration = duration;
    });
  };

  getDuration = () => this.duration;
}

const userStore = () => new UserStore();

export default userStore;
